import { FC, useState, useEffect } from "react";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import { IDocumentCreatorProps } from "../../../../@types/documents";
import { useTabsContext } from "../../../../system/providers/tabsProvider";
import { InvoiceDataProvider } from "../../../../Services/DataProviders/InvoiceDataProvider";
import InvoiceUpdateForm from "./InvoiceUpdateForm";
import { IInvoiceUpdateDTO, IInvoiceCreateDTO, IInvoiceGetDTO } from "../../../../libs/coreapi-dto/documents/invoice";
import { getInvoiceItemUpdateDTO } from "../../../../libs/core-api-requests/customRequest/InvoiceItemUpdateRequest";
import { IInvoiceItemEditDTO, IInvoiceItemGetDTO } from "../../../../libs/coreapi-dto/documents/invoiceItem";
import { v4 as uuidv4 } from "uuid";
import { useTestApiContext } from "../../../../system/providers/testApiProvider";
import { DateTime } from "luxon";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { DocumentOperationSpinner } from "../../../../components/DocumentOperationSpinner/DocumentOperationSpinner";
import { useAlertsModal } from "../Components/AlertsModal/useAlertsModal";
import AlertsModal from "../../../../components/modalWindows/AlertsModal";

interface ICreateDocument {
    createCallback: (invoice) => void;
    saveCallback: (invoice) => void;
}

interface IEditDocument {
    id: string | undefined;
    document: any;
    documentItems: any;
    documentUpdate: any;
}

interface ICopyDocument {
    id: string | undefined;
    document: any;
    documentItems: any;
    documentUpdate: any;
}

const InvoiceCreatorView: FC<IDocumentCreatorProps<IInvoiceCreateDTO>> = (props) => {
    const appContext = useAppContext();
    const userContext = useUserContext()
    const tabsContext = useTabsContext();
    const testApiContext = useTestApiContext();

    const idp = new InvoiceDataProvider(appContext.coreApiService);
    const [viewState, setViewState] = useState<GridStateType>(props.variant);
    const [id, setId] = useState<string | undefined>(props.idGlobal);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [createDocument, setCreateDocument] = useState<ICreateDocument | null>(null);
    const [editDocument, setEditDocument] = useState<IEditDocument | null>(null);
    const [copyDocument, setCopyDocument] = useState<ICopyDocument | null>(null);
    const { modalConfig, constructorAlertModal } = useAlertsModal();
    const [focusItemId, setFocusItemId] = useState<string>();

    const prepareDataForSave = (data: IInvoiceUpdateDTO) => {
        return {
            ...data,
            supplierDocDate: data.supplierDocDate instanceof DateTime
                ? data.supplierDocDate.toF3DateTimeServerSide(userContext)
                : undefined,
            supplierBillDocte: data.supplierBillDocDate instanceof DateTime
                ? data.supplierBillDocDate.toF3DateTimeServerSide(userContext)
                : undefined
        };
    };

    const getInvoice = () => {
        if (!id) return

        setIsLoading(true);
        try {
            idp.getById(id as string, async (invoiceDocument) => {
                getInvoiceItemUpdateDTO(invoiceDocument.idGlobal as string, (e) => {
                    const itemsUpdate: IInvoiceItemEditDTO[] = [];
                    e.forEach((item) => {
                        itemsUpdate.push({ ...item });
                    });
    
                    const documentItems: IInvoiceItemGetDTO[] = [];
                    invoiceDocument.items.forEach((item, index) => {
                        documentItems.push({ ...item, isGnvls: e[index].isGnvls });
                    });
    
                    const documentUpdate: IInvoiceUpdateDTO = {
                        idGlobal: invoiceDocument.idGlobal,
                        documentDate: invoiceDocument.documentDate.toF3DateTimeServerSide(userContext),
                        idSupplierGlobal: invoiceDocument.supplier.idGlobal,
                        idStoreGlobal: invoiceDocument.store.idGlobal,
                        idGosContractExternalGlobal: invoiceDocument.gosContract?.idGlobal ?? undefined,
                        idPricingModelGlobal: invoiceDocument.pricingModel.idGlobal,
                        supplierDocNumber: invoiceDocument.supplierDocNumber,
                        supplierDocDate: invoiceDocument.supplierDocDate instanceof DateTime
                        ? invoiceDocument.supplierDocDate.toF3DateTimeServerSide(userContext)
                        : undefined,
                        supplierBillDocNumber: invoiceDocument.supplierBillDocNumber,
                        supplierBillDocDate: invoiceDocument.supplierBillDocDate instanceof DateTime
                        ? invoiceDocument.supplierBillDocDate.toF3DateTimeServerSide(userContext)
                        : undefined,
                        payerName: invoiceDocument.payerName,
                        isGiftInvoice: invoiceDocument.isGiftInvoice,
                        datePay: invoiceDocument.datePay,
                        isInternetOrder: invoiceDocument.isInternetOrder,
                        internetOrderDate: invoiceDocument.internetOrderDate,
                        internetOrderNumber: invoiceDocument.internetOrderNumber,
                        customerName: invoiceDocument.customerName,
                        customerPhone: invoiceDocument.customerPhone,
                        items: itemsUpdate,
                    };
                    setEditDocument({
                        id: id,
                        document: invoiceDocument,
                        documentItems: documentItems,
                        documentUpdate: documentUpdate,
                    });
                    setIsLoading(false);
                }, invoiceDocument);
            });
        } catch (error) {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const initializeComponent = async () => {
            setIsLoading(true);
            
            if (viewState === "create") {
                const createCallback = (invoice: IInvoiceCreateDTO) => {
                    const tabToClose = tabsContext.currentTab;
                    setIsSubmitting(true);
                    invoice = prepareDataForSave(invoice)
                if (props.isTest) {
                        idp.create(
                            invoice,
                            (id) => {
                                setId(id);
                                props.createdCallback?.();
                                setViewState("create");
                                tabToClose && tabsContext.close(tabToClose);
                                setIsSubmitting(false);
                            },
                            (value) => {
                                testApiContext.setApiInfo(value);
                            }, (e)=>{
                                setIsSubmitting(false);
                            }
                        );
                    } else {
                        idp.create(invoice, (id) => {
                            setId(id);
                            props.createdCallback?.();
                            setViewState("create");
                            tabsContext.currentTab && tabsContext.closeCurrent();
                            setIsSubmitting(false);
                        }, ()=>{}, (e)=>{
                            setIsSubmitting(false);
                        });
                    }
                };
                const saveCallback = (invoice: IInvoiceCreateDTO) => {
                    setIsSubmitting(true);
                    invoice = prepareDataForSave(invoice)
                idp.create(invoice, (id) => {
                        setId(id);
                        setViewState("edit");
                        props.createdCallback?.();
                        getInvoice()
                        setIsSubmitting(false);
                    }, ()=>{}, (e)=>{
                        setIsSubmitting(false);
                    });
                };
                setCreateDocument({
                    createCallback: createCallback,
                    saveCallback: saveCallback,
                });
                setIsLoading(false);
            }

            if (viewState === "edit") {
                getInvoice();
            }

            if (viewState === "copy") {
                idp.getById(id as string, (invoiceDocument) => {
                    getInvoiceItemUpdateDTO(invoiceDocument.idGlobal as string, (e) => {
                        const itemsUpdate: IInvoiceItemEditDTO[] = [];
                        const itemsInvoiceDocumentCopy: IInvoiceItemGetDTO[] = [];
                        e.forEach((item) => {
                            const idGlobalNew = uuidv4();
                            itemsUpdate.push({
                                idGlobal: idGlobalNew,
                                idGoodsGlobal: item.idGoodsGlobal,
                                idScalingRatioGlobal: item.idScalingRatioGlobal,
                                idSeriesGlobal: item.idSeriesGlobal,
                                idGosContractExternalGlobal: item.idGosContractExternalGlobal,
                                quantity: item.quantity,
                                producerPrice: item.producerPrice,
                                productMargin: item.productMargin,
                                goodsCode: item.goodsCode,
                                producer: item.producer,
                                supplierCostInfo: item.supplierCostInfo,
                                retailCostInfo: item.retailCostInfo,
                                supplierGoodsCode: item.supplierGoodsCode,
                                gtdNumber: item.gtdNumber,
                                isLs: item.isLs,
                                barCode: item.barCode,
                                registerPrice: item.registerPrice,
                                isKiz: item.isKiz,
                                kizCount: undefined,
                                kizBoxCount: undefined,
                                kizBoxes: [],
                                kizBoxesGisMt: [],
                                kizs: [],
                                denominator: item.denominator,
                                numerator: item.numerator,
                                dateCreated: DateTime.now(),
                                actualQuantity: item.actualQuantity,
                                damageQuantity: item.damageQuantity,
                                defectQuantity: item.defectQuantity,
                                shortageQuantity: item.shortageQuantity,
                                isPieceAccountingKizOsu: item.isPieceAccountingKizOsu,
                                idStorePlaceGlobal: item.idStorePlaceGlobal,
                                idFinancialSecurityGlobal: item.idFinancialSecurityGlobal
                            });
                            const itemFound = invoiceDocument.items.find((x) => x.idGlobal === item.idGlobal);
                            if (itemFound) {
                                itemsInvoiceDocumentCopy.push({
                                    idGlobal: idGlobalNew,
                                    goods: itemFound.goods,
                                    scalingRatio: itemFound.scalingRatio,
                                    series: itemFound.series,
                                    goodsCode: itemFound.goodsCode,
                                    producer: itemFound.producer,
                                    idGosContractGlobal: itemFound.idGosContractGlobal,
                                    quantity: itemFound.quantity,
                                    actualQuantity: itemFound.actualQuantity,
                                    damageQuantity: itemFound.damageQuantity,
                                    defectQuantity: itemFound.defectQuantity,
                                    shortageQuantity: itemFound.shortageQuantity,
                                    producerPrice: itemFound.producerPrice,
                                    productMargin: itemFound.retailCostInfo?.adprice ?? itemFound.productMargin,
                                    supplierCostInfo: itemFound.supplierCostInfo,
                                    retailCostInfo: itemFound.retailCostInfo,
                                    supplierGoodsCode: itemFound.supplierGoodsCode,
                                    gtdNumber: itemFound.gtdNumber,
                                    barCode: itemFound.barCode,
                                    isLs: itemFound.isLs,
                                    isGnvls: itemFound.isGnvls,
                                    registerPrice: itemFound.registerPrice,
                                    isKiz: itemFound.isKiz,
                                    isKizBox: itemFound.isKizBox,
                                    kizBoxCount: itemFound.kizBoxCount,
                                    kizCount: itemFound.kizCount,
                                    denominator: itemFound.denominator,
                                    numerator: itemFound.numerator,
                                    dateCreated: DateTime.now(),
                                    bestBefore: itemFound.bestBefore,
                                    seriesNumber: itemFound.seriesNumber,
                                    isPieceAccountingKizOsu: itemFound.isPieceAccountingKizOsu,
                                    storePlace: itemFound.storePlace,
                                    kizBoxesGisMt: itemFound.kizBoxesGisMt,
                                    financialSecurity: itemFound.financialSecurity,
                                    idFinancialSecurityGlobal: itemFound.idFinancialSecurityGlobal
                                });
                            }
                        });
                        const idGlobalNew = uuidv4();
                        const documentUpdate: IInvoiceUpdateDTO = {
                            idGlobal: idGlobalNew,
                            documentDate: invoiceDocument.documentDate.toF3DateTimeServerSide(userContext),
                            idSupplierGlobal: invoiceDocument.supplier.idGlobal,
                            isGiftInvoice: invoiceDocument.isGiftInvoice,
                            idStoreGlobal: invoiceDocument.store.idGlobal,
                            idPricingModelGlobal: invoiceDocument?.pricingModel?.idGlobal,
                            payerName: invoiceDocument.payerName,
                            isInternetOrder: invoiceDocument.isInternetOrder,
                            internetOrderDate: invoiceDocument.internetOrderDate,
                            internetOrderNumber: invoiceDocument.internetOrderNumber,
                            customerName: invoiceDocument.customerName,
                            customerPhone: invoiceDocument.customerPhone,
                            items: itemsUpdate,
                        };
                        const invoiceDocumentCopy: IInvoiceGetDTO = {
                            idGlobal: idGlobalNew,
                            documentDate: invoiceDocument.documentDate.toF3DateTimeServerSide(userContext),
                            displayName: invoiceDocument.displayName,
                            documentState: invoiceDocument.documentState,
                            documentStateModified: invoiceDocument.documentStateModified,
                            mnemocode: '',
                            supplier: invoiceDocument.supplier,
                            store: invoiceDocument.store,
                            pricingModel: invoiceDocument.pricingModel,
                            averagePercentSumIncVat: invoiceDocument.averagePercentSumIncVat,
                            payerName: invoiceDocument.payerName,
                            isInternetOrder: invoiceDocument.isInternetOrder,
                            internetOrderDate: invoiceDocument.internetOrderDate,
                            internetOrderNumber: invoiceDocument.internetOrderNumber,
                            customerName: invoiceDocument.customerName,
                            customerPhone: invoiceDocument.customerPhone,
                            items: itemsInvoiceDocumentCopy,
                            retailCostInfo: invoiceDocument.retailCostInfo,
                            supplierCostInfo: invoiceDocument.supplierCostInfo,
                        };
                        setCopyDocument({
                            id: id,
                            document: invoiceDocumentCopy,
                            documentItems: invoiceDocumentCopy.items,
                            documentUpdate: documentUpdate,
                        });
                        setIsLoading(false);
                    });
                });
            }
        };

        initializeComponent();
    }, [viewState]);

    if (isLoading) {
        return (
            <div >
                <DocumentOperationSpinner xl data-testid="invoice-creator-loading" />                
            </div>
        );
    }

    return (
        <>
            {viewState === "create" ? (
                createDocument && (
                    <InvoiceUpdateForm
                        isSubmitting={isSubmitting}
                        document={null}
                        documentItems={null}
                        documentUpdate={null}
                        isTest={props.isTest}
                        variant="create"
                        ok={(invoice) => {
                            createDocument?.createCallback(invoice);
                        }}
                        save={(invoice) => {
                            createDocument?.saveCallback(invoice);
                        }}
                        cancel={() => {
                            tabsContext.closeCurrent();
                        }}
                        permission={props.permission}
                    />
                )
            ) : viewState === "edit" ? (
                editDocument && (
                    <InvoiceUpdateForm
                        isSubmitting={isSubmitting}
                        idGlobal={editDocument.id}
                        variant="edit"
                        document={editDocument.document}
                        documentItems={editDocument.documentItems}
                        documentUpdate={editDocument.documentUpdate}
                        ok={(invoice, action, alerts) => {
                            constructorAlertModal(
                                alerts ?? [],
                                action,
                                {
                                    onCancel: () => {},
                                    onConfirm: () => {
                                        const tabToClose = tabsContext.currentTab;
                                        setIsSubmitting(true);
                                        invoice = prepareDataForSave(invoice);
                                        idp.update(id as string, invoice, (e) => {
                                            if (e.respType === 'isFailed') return setIsSubmitting(false);

                                            props.createdCallback?.(invoice);
                                            tabToClose && tabsContext.close(tabToClose);
                                            setIsSubmitting(false);
                                        });
                                    }
                                }
                            );
                        }}
                        save={(invoice, action, alerts) => {
                             constructorAlertModal(
                                alerts ?? [],
                                action,
                                {
                                    onCancel: () => {},
                                    onConfirm: () => {
                                        setIsSubmitting(true);
                                        invoice = prepareDataForSave(invoice);
                                        idp.update(id as string, invoice, (e) => {
                                            if (e.respType === 'isFailed') return setIsSubmitting(false);

                                            props.createdCallback?.(invoice);
                                            getInvoice();
                                            setIsSubmitting(false);
                                        });
                                    }
                                }
                            );
                            
                        }}
                        cancel={() => {
                            tabsContext.closeCurrent();
                        }}
                        permission={props.permission}
                        focusItemId={focusItemId}
                    />
                )
            ) : viewState === "copy" ? (
                copyDocument && (
                    <InvoiceUpdateForm
                        isSubmitting={isSubmitting}
                        variant="copy"
                        idGlobal={copyDocument.id}
                        document={copyDocument.document}
                        documentItems={copyDocument.documentItems}
                        documentUpdate={copyDocument.documentUpdate}
                        ok={(invoice, action, alerts) => {
                            constructorAlertModal(
                                alerts ?? [],
                                action,
                                {
                                    onCancel: () => {},
                                    onConfirm: () => {
                                        const tabToClose = tabsContext.currentTab;
                                        setIsSubmitting(true);
                                        invoice = prepareDataForSave(invoice);
                                        idp.create(invoice, (id) => {
                                            setId(id);
                                            props.createdCallback?.();
                                            tabToClose && tabsContext.close(tabToClose);
                                            setIsSubmitting(false);
                                        }, ()=>{}, (e)=>{
                                            setIsSubmitting(false);
                                        });
                                    }
                                }
                            );
                            
                        }}
                        save={(invoice, action, alerts) => {
                           constructorAlertModal(
                                alerts ?? [],
                                action,
                                {
                                    onCancel: () => {},
                                    onConfirm: () => {
                                        setIsSubmitting(true);
                                        invoice = prepareDataForSave(invoice);
                                        idp.create(invoice, (id) => {
                                            setId(id);
                                            setViewState("edit");
                                            props.createdCallback?.();
                                            setIsSubmitting(false);
                                        }, ()=>{}, (e)=>{
                                            setIsSubmitting(false);
                                        });
                                    }
                                }
                            );
                        }}
                        cancel={() => {
                            tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
                        }}
                        permission={props.permission}
                        focusItemId={focusItemId}
                    />
                )
            ) : (
                <></>
            )}
            {modalConfig && (
                <AlertsModal
                    header={modalConfig.header}
                    warning={modalConfig.warning}
                    items={modalConfig.items}
                    question={modalConfig.question}
                    action={modalConfig.action}
                    onConfirm={modalConfig.onConfirm}
                    onCancel={modalConfig.onCancel}
                    textAreaInputStyle={modalConfig.textAreaInputStyle}
                    onFocusItem={setFocusItemId}
                />
            )}
        </>
    );
};

export default InvoiceCreatorView;
